import { traceCall } from './callTrace.js';

//********************* CALL AUDIO ********************************//
// Where the call is heard (earpiece, speaker, a headset), whether the app may
// use the microphone, and what the phone should do while a call is on screen
// (stay awake; go dark at the ear). All of it is native: a web page cannot
// pick the earpiece or the loudspeaker on iOS or Android - WKWebView has no
// API for it, Android's WebView no working one - so the GhVoip plugin does it
// with AVAudioSession and AudioManager/Telecom, for a call placed from this
// page and for a call the native engine runs alike.
//
// The screen never shows what it asked for, only what the phone reports
// (getAudioRoute / the audioRouteChanged event). A Bluetooth headset that
// connects mid-call, the system's own call screen switching to the speaker,
// a route that refused to change - the button follows the real route.
//
// Everything here is inert in a browser, and in an app build whose GhVoip has
// none of these methods yet: the speaker button is simply not shown there.
export const callAudioMixin = {

    // Whether the installed app's GhVoip has a method - read from the plugin
    // headers Capacitor injects, so an older build is detected without
    // calling into it and logging "not implemented".
    _voipSupports(method) {
        if(!this.isNativeShell?.()) return false;
        const headers = window.Capacitor?.PluginHeaders;
        const header = Array.isArray(headers) ? headers.find((entry) => entry.name === 'GhVoip') : null;
        return Boolean(header?.methods?.some((entry) => entry.name === method));
    },

    //********************* CALL SCREEN LIFECYCLE ********************************//
    // dialogMixin calls these as the call screen opens, ends and closes.
    onCallScreenOpened() {
        if(!this._voipSupports('getAudioRoute')) return;
        this._setCallActive(true);
        this._listenForAudioRoutes();
        this.refreshAudioRoute();
        if(this._callScreen?.owner === 'native') this._checkMicrophoneForNativeCall();
    },

    onCallScreenEnded() {
        this._setCallActive(false);
    },

    onCallScreenClosed() {
        this._setCallActive(false);
    },

    _setCallActive(active) {
        if(this._callActive === active) return;
        this._callActive = active;
        if(!this._voipSupports('setCallActive')) return;
        this._safe(() => this._voip().setCallActive({ active }));
    },

    //********************* ROUTES ********************************//
    _listenForAudioRoutes() {
        if(this._audioRoutesListening) return;
        this._audioRoutesListening = true;

        this._voip().addListener('audioRouteChanged', (state) => this._applyAudioRoute(state));

        // Coming back to the app: the route may have changed meanwhile (a
        // headset unplugged, the speaker picked on the lock screen).
        document.addEventListener('visibilitychange', () => {
            if(document.visibilityState === 'visible' && this._callScreen) this.refreshAudioRoute();
        });
    },

    async refreshAudioRoute() {
        const state = await this._safe(() => this._voip().getAudioRoute());
        this._applyAudioRoute(state);
    },

    // * state ({ route, routes: [{ id, type, name }], activeId })
    _applyAudioRoute(state) {
        if(!state || !Array.isArray(state.routes)) return;
        this._audioRouteState = {
            route: state.route || 'unknown',
            routes: state.routes,
            activeId: state.activeId || null,
            pending: false,
        };
        this.updateDialogState({ audio: this._audioRouteState });
    },

    // The speaker button with only the phone's own earpiece and speaker.
    toggleSpeaker() {
        const audio = this._audioRouteState;
        if(!audio) return;
        return this.selectAudioRoute(audio.route === 'speaker' ? 'earpiece' : 'speaker');
    },

    // * route ('earpiece'|'speaker'|'bluetooth'|'headset'), deviceId (String|null)
    async selectAudioRoute(route, deviceId = null) {
        const before = this._audioRouteState;
        if(!before || !this._voipSupports('setAudioRoute')) return;
        if(this._routeChange) return this._routeChange;

        this.updateDialogState({ audio: { ...before, pending: true } });
        traceCall('audioRoute', { requested: route, from: before.route });

        this._routeChange = this._safe(() => this._voip().setAudioRoute({ route, deviceId: deviceId || undefined }))
            .then((result) => {
                this._routeChange = null;
                // What the phone did, not what was asked - and the old state
                // when it could not even say.
                this._applyAudioRoute(result?.routes ? result : before);
                const reached = result?.route === route && (!deviceId || !result.activeId || result.activeId === deviceId);
                if(!reached) {
                    traceCall('audioRouteRefused', { requested: route, actual: result?.route || 'unknown' });
                    this._callScreen?.showHint(this._t('hintRouteFailed'));
                }
            });
        return this._routeChange;
    },

    //********************* MICROPHONE ********************************//
    // Before placing or answering a call: the reason it cannot go ahead
    // ('micDenied'), or null. Anything short of "granted" is asked for: the
    // system shows its prompt (with the app's own wording) when it still
    // may, and answers "denied" at once when the user has said no for good -
    // only Settings can change that, and the screen says so.
    async checkMicrophoneForCall() {
        if(this._voipSupports('getMicrophonePermission')) {
            let state = (await this._safe(() => this._voip().getMicrophonePermission()))?.state;
            if(state && state !== 'granted' && this._voipSupports('requestMicrophonePermission')) {
                state = (await this._safe(() => this._voip().requestMicrophonePermission()))?.state;
            }
            traceCall('microphone', { state: state || 'unknown' });
            return state === 'denied' ? 'micDenied' : null;
        }

        // A browser (or an older app): only a definite "denied" stops the
        // call here; getUserMedia asks for anything else on its own.
        try {
            const status = await navigator.permissions?.query({ name: 'microphone' });
            return status?.state === 'denied' ? 'micDenied' : null;
        } catch (err) {
            return null;
        }
    },

    // A call the native engine answered goes ahead without the microphone
    // (the user said no to it once); the screen says the other side hears
    // nothing, with the way to fix it.
    async _checkMicrophoneForNativeCall() {
        if(!this._voipSupports('getMicrophonePermission')) return;
        const state = (await this._safe(() => this._voip().getMicrophonePermission()))?.state;
        if(state === 'denied') this.updateDialogState({ micBlocked: true });
    },

    openAppSettings() {
        if(this._voipSupports('openSettings')) this._safe(() => this._voip().openSettings());
    },
};
